/**
 * Predefined document-set groupings recognized by the backend's
 * `[dbo].[WY_tvf_getDocumentsList]` SQL function (`@viewtype` parameter,
 * bound from this app's `_listType` query param — see
 * `document-api.service.ts`'s `getDocListDateEx`/`getDocListDate`/
 * `getDocListSlice`). Transcribed verbatim from that function's own header
 * comment (SSMS, author SergiiK, created 2020-04-25) — its `WHERE` clause
 * only recognizes the values below; any other `_listType` value matches
 * none of its branches and returns **zero rows**, not "no filter."
 *
 * REGRESSION HISTORY (2026-08-24): both call sites below sent `_listType=100`
 * for years — not a value this enum (or the SQL function) ever recognized.
 * It "worked" only because of an unrelated bug: the frontend sent the query
 * param as `_listtype` (lowercase) while the backend read `_listType`
 * (capital T), so the value never actually reached the backend — it always
 * fell back to `0`/`ALL`. Fixing that casing bug (Phase 13, PR-1/PR-2,
 * `_listtype` → `_listType`) let `100` reach the backend for the first
 * time, which zeroed every list response. Root-caused via
 * `webyto_api-express4-TS-20250331`'s `BUG-DATE-5`/`BUG-DL-XAPPLY-1`
 * investigation in `docs/OPEN_ISSUES.md`. Fixed here by sending the
 * value this enum actually defines (`ALL`) instead of the never-valid `100`.
 *
 * This grouping is a legacy feature never surfaced as an actual selector in
 * the Angular UI (every current caller always sends `ALL`) — kept here,
 * with its `DOCTYPEID` membership preserved via
 * `DOCUMENT_LIST_VIEW_TYPE_DOCTYPES` below, for when a predefined
 * document-set selector is (re)implemented.
 */
export enum DocumentListViewType {
  /** No filter — every document type. The only value any caller in this app currently sends. */
  ALL = 0,
  /** Invoices and proformas. `DOCTYPEID IN (1, 12)`. */
  INVOICES_AND_PROFORMAS = 1,
  /** Receipts. `DOCTYPEID IN (2, 3, 4, 5)`. */
  RECEIPTS = 2,
  /** Payments. `DOCTYPEID IN (8, 9, 11)`. */
  PAYMENTS = 3,
  /**
   * "Species" — label transcribed verbatim from the SQL function's own
   * comment; not otherwise confirmed against WebYTO business terminology.
   * `DOCTYPEID IN (10, 14)`.
   */
  SPECIES = 4,
}

/**
 * `DOCTYPEID` membership of each `DocumentListViewType`, for building a
 * future document-set selector or validating a value client-side before
 * it's sent as `_listType`. `null` means "no restriction" (`ALL`).
 */
export const DOCUMENT_LIST_VIEW_TYPE_DOCTYPES: Readonly<Record<DocumentListViewType, readonly number[] | null>> = {
  [DocumentListViewType.ALL]: null,
  [DocumentListViewType.INVOICES_AND_PROFORMAS]: [1, 12],
  [DocumentListViewType.RECEIPTS]: [2, 3, 4, 5],
  [DocumentListViewType.PAYMENTS]: [8, 9, 11],
  [DocumentListViewType.SPECIES]: [10, 14],
};
